import { useEffect, useRef, useState } from "react";
import { Play, Pause, Upload, Volume2, VolumeX } from "lucide-react";
import { drawCaption, findCaption } from "@/lib/render";
import { fmtShort } from "@/lib/subtitles";

const ASPECTS = [["original", "Original"], ["9:16", "9:16"], ["16:9", "16:9"], ["1:1", "1:1"]];

function useCaptionCanvas(canvasRef, videoRef, captions, style, fontTick) {
  const dataRef = useRef({ captions, style });
  dataRef.current = { captions, style, fontTick };
  useEffect(() => {
    let raf;
    let lastKey = "";
    const loop = () => {
      const c = canvasRef.current;
      const v = videoRef.current;
      if (c && v) {
        const r = c.getBoundingClientRect();
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        const W = Math.round(r.width * dpr);
        const H = Math.round(r.height * dpr);
        const { captions: caps, style: st, fontTick: ft } = dataRef.current;
        const t = v.currentTime;
        const key = `${t}|${W}x${H}|${ft}|${caps.length}|${JSON.stringify(st)}|${caps.map((x) => x.start + x.text + x.end).join("").length}`;
        if (key !== lastKey || !v.paused) {
          lastKey = key;
          if (c.width !== W) c.width = W;
          if (c.height !== H) c.height = H;
          const ctx = c.getContext("2d");
          ctx.clearRect(0, 0, W, H);
          drawCaption(ctx, W, H, findCaption(caps, t), t, st);
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [canvasRef, videoRef]);
}

export const VideoStage = ({ videoRef, videoUrl, meta, aspect, setAspect, captions, style, onTime, fontTick, onUpload }) => {
  const canvasRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [t, setT] = useState(0);
  useCaptionCanvas(canvasRef, videoRef, captions, style, fontTick);

  const ratio = aspect === "original" ? `${meta.w} / ${meta.h}` : aspect.replace(":", " / ");
  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    v.paused ? v.play() : v.pause();
  };

  return (
    <div className="p-3 md:p-5 border-b border-[var(--cc-border)]" data-testid="video-stage">
      <div className="flex justify-center bg-black/40 rounded-lg">
        <div className="relative max-w-full bg-black overflow-hidden rounded-md h-[30vh] md:h-[min(50vh,560px)]" style={{ aspectRatio: ratio }}>
          {videoUrl ? (
            <>
              <video ref={videoRef} src={videoUrl} playsInline muted={muted} data-testid="preview-video"
                className={`absolute inset-0 w-full h-full ${aspect === "original" ? "object-contain" : "object-cover"}`}
                onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onClick={toggle}
                onTimeUpdate={(e) => { setT(e.target.currentTime); onTime(e.target.currentTime); }}
                onSeeked={(e) => { setT(e.target.currentTime); onTime(e.target.currentTime); }} />
              <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" data-testid="caption-canvas" />
            </>
          ) : (
            <button onClick={onUpload} data-testid="stage-upload-btn"
              className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-[var(--cc-text-2)] hover:text-white px-6 text-center">
              <Upload className="h-8 w-8" />
              <span className="text-sm font-semibold">Upload a video to start</span>
            </button>
          )}
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button onClick={toggle} disabled={!videoUrl} aria-label={playing ? "Pause" : "Play"} data-testid="play-pause-btn"
          className="h-11 w-11 shrink-0 rounded-full bg-[var(--cc-accent)] text-black grid place-items-center disabled:opacity-40">
          {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
        </button>
        <input type="range" min={0} max={meta.duration || 0} step={0.01} value={t} disabled={!videoUrl} aria-label="Seek"
          data-testid="seek-slider" onChange={(e) => { videoRef.current.currentTime = +e.target.value; }} className="flex-1" />
        <span className="font-mono-ts text-xs text-amber-400 w-[92px] text-right" data-testid="time-display">{fmtShort(t)} / {fmtShort(meta.duration)}</span>
        <button onClick={() => setMuted(!muted)} aria-label={muted ? "Unmute" : "Mute"} data-testid="mute-btn"
          className="h-10 w-10 grid place-items-center rounded-md text-[var(--cc-text-2)] hover:bg-[var(--cc-elev)]">
          {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </button>
      </div>
      <div className="mt-2 flex items-center gap-1.5" role="group" aria-label="Preview frame">
        <span className="text-[11px] uppercase tracking-wider text-[var(--cc-muted)] mr-1">Frame</span>
        {ASPECTS.map(([id, label]) => (
          <button key={id} onClick={() => setAspect(id)} data-testid={`aspect-${id.replace(":", "x")}`} aria-pressed={aspect === id}
            className={`h-8 px-3 rounded text-xs font-semibold border ${aspect === id ? "border-[var(--cc-accent)] text-[var(--cc-accent)] bg-amber-500/10" : "border-[var(--cc-border-strong)] text-[var(--cc-text-2)]"}`}>
            {label}
          </button>
        ))}
      </div>
    </div>
  );
};
